import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Skeleton from "../../../components/ui/Skeleton";
import Modal from "../../../components/ui/Modal";
import StatusBadge from "../../../components/ui/StatusBadge";
import TextInput from "../../../components/ui/TextInput";
import FormField from "../../../components/ui/FormField";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import { toast } from "react-toastify";
import { getStoredAuthUser } from "../../../services/authService";
import {
  approveVisitorEntry,
  getResidentVisitorRequests,
  rejectVisitorEntry,
} from "../../../services/visitorService";
import { rejectionReasonSchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import { Car, RefreshCw, CheckCircle, XCircle, Package } from "lucide-react";
import styles from "./ResidentVisitors.module.css";

const VisitorCard = ({ row, onApprove, onReject, busy }) => (
  <div className={styles.row}>
    <div className={styles.top}>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        {row.photo_url && (
          <img
            src={row.photo_url}
            alt={row.visitor_name}
            style={{
              width: "52px",
              height: "52px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #6366f1",
              flexShrink: 0,
            }}
          />
        )}
        <div>
          <div className={styles.name}>
            {row.visitor_name} <span className={styles.muted}>{row.visitor_phone}</span>
            {row.vehicle_number ? (
              <span style={{ marginLeft: "8px", color: "#d97706", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Car size={13} /> {row.vehicle_number}
              </span>
            ) : null}
          </div>
          <div className={styles.meta}>
            {row.pass_code ? (
              <span style={{ fontWeight: 800, color: "#4f46e5", marginRight: "6px" }}>
                Pass: {row.pass_code} •
              </span>
            ) : null}
            {row.entry_type || "VISITOR"} {row.purpose ? `• ${row.purpose}` : ""}
          </div>
          <div className={styles.meta}>
            Dept: {row.unit_number || "-"}
            {row.cabin_code ? ` • Cabin: ${row.cabin_code}` : ""}
            {row.desk_code ? ` • Desk: ${row.desk_code}` : ""}
          </div>
          <div className={styles.meta}>
            Requested: {row.requested_at ? new Date(row.requested_at).toLocaleString() : "-"}
          </div>
          {row.delivery_action ? (
            <div className={styles.meta} style={{ color: "var(--color-dark-bg)", fontWeight: 700 }}>
              Action: {row.delivery_action}
            </div>
          ) : null}
          {row.status === "REJECTED" && row.rejection_reason ? (
            <div className={styles.meta} style={{ color: "#dc2626" }}>Rejection: {row.rejection_reason}</div>
          ) : null}
        </div>
      </div>

      <div className={styles.actions}>
        <StatusBadge status={row.status} />
        {row.status === "PENDING" ? (
          row.entry_type === "DELIVERY" ? (
            <>
              <Button size="sm" onClick={() => onApprove(row.id, "ALLOW_IN")} disabled={busy}>
                <CheckCircle size={14} style={{ marginRight: "4px" }} /> Allow In
              </Button>
              <Button variant="outline" size="sm" onClick={() => onApprove(row.id, "COLLECT_AT_GATE")} disabled={busy}>
                <Package size={14} style={{ marginRight: "4px" }} /> Collect at Gate
              </Button>
              <Button variant="danger" size="sm" onClick={() => onReject(row.id)} disabled={busy}>
                <XCircle size={14} style={{ marginRight: "4px" }} /> Reject
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => onApprove(row.id, "APPROVE")} disabled={busy}>
                <CheckCircle size={14} style={{ marginRight: "4px" }} /> Approve
              </Button>
              <Button variant="outline" size="sm" onClick={() => onReject(row.id)} disabled={busy}>
                <XCircle size={14} style={{ marginRight: "4px" }} /> Reject
              </Button>
            </>
          )
        ) : null}
      </div>
    </div>
  </div>
);

const ResidentVisitors = () => {
  const authUser = getStoredAuthUser();
  const residentUserId = Number(authUser?.user_id) || 0;

  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [rows, setRows] = useState([]);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const pending = useMemo(() => rows.filter((r) => r.status === "PENDING"), [rows]);
  const history = useMemo(() => rows.filter((r) => r.status !== "PENDING"), [rows]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      if (!residentUserId) {
        setRows([]);
        return;
      }
      const data = await getResidentVisitorRequests({ residentUserId });
      setRows(data || []);
    } catch (error) {
      console.error("LOAD RESIDENT VISITORS ERROR:", error);
      toast.error(error?.data?.error || error?.data?.message || "Unable to load requests");
    } finally {
      setLoading(false);
    }
  }, [residentUserId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleApprove = async (id, action = "APPROVE") => {
    setActionId(id);
    try {
      const res = await fetch(`/api/public/visitor/entries/${id}/action`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, user_id: residentUserId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(action === "COLLECT_AT_GATE" ? "Marked: Collect at Gate" : "Request approved!");
        await loadPage();
      } else {
        await approveVisitorEntry(id, { residentUserId });
        toast.success("Approved");
        await loadPage();
      }
    } catch (error) {
      console.error("APPROVE ERROR:", error);
      toast.error(error?.data?.error || error?.data?.message || "Unable to approve");
    } finally {
      setActionId(null);
    }
  };

  const openReject = (id) => {
    setRejectingId(id);
    setRejectionReason("");
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    const parsed = rejectionReasonSchema.safeParse({ rejectionReason });
    if (!parsed.success) return toast.error(zodErrorToFirstMessage(parsed.error));

    setActionId(rejectingId);
    try {
      await rejectVisitorEntry(rejectingId, {
        residentUserId,
        rejectionReason: parsed.data.rejectionReason,
      });
      toast.success("Request rejected");
      setRejectModalOpen(false);
      setRejectingId(null);
      await loadPage();
    } catch (error) {
      console.error("REJECT ERROR:", error);
      toast.error(error?.data?.error || error?.data?.message || "Unable to reject");
    } finally {
      setActionId(null);
    }
  };

  return (
    <AdminLayout title="Resident Visitors">
      <div className={styles.page}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Visitor Requests</h1>
            <p className={styles.subTitle}>Approve or reject guest entry requests for your unit in real-time.</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadPage}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} style={{ marginRight: "4px" }} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className={styles.card}>
            <Skeleton variant="card" count={3} />
          </div>
        ) : (
          <>
            <div className={styles.card}>
              <h2 className={styles.sectionTitle}>
                Pending Requests ({pending.length})
              </h2>
              {pending.length === 0 ? (
                <EmptyState
                  title="No pending requests"
                  description="There are currently no visitors waiting at the gate for your approval."
                />
              ) : (
                <div className={styles.list}>
                  {pending.map((row) => (
                    <VisitorCard
                      key={row.id}
                      row={row}
                      busy={actionId === row.id}
                      onApprove={handleApprove}
                      onReject={openReject}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h2 className={styles.sectionTitle}>Request History ({history.length})</h2>
              {history.length === 0 ? (
                <EmptyState
                  title="No history yet"
                  description="Past visitor approvals and rejections will appear here."
                />
              ) : (
                <div className={styles.list}>
                  {history.map((row) => (
                    <VisitorCard
                      key={row.id}
                      row={row}
                      busy={false}
                      onApprove={() => { }}
                      onReject={() => { }}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Visitor Request"
      >
        <div className={styles.rejectBox}>
          <FormField
            label="Rejection Reason"
            required
            helperText="This reason will be recorded in audit logs and shown to the security guard."
          >
            <TextInput
              placeholder="e.g. Not available / Unknown guest / Wrong flat"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              autoFocus
            />
          </FormField>
          <div className={styles.modalActions} style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              disabled={actionId === rejectingId}
              loading={actionId === rejectingId}
            >
              Confirm Rejection
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default ResidentVisitors;
